import React, { useState, useEffect } from 'react';
import { formMasterAPI, commonAPI } from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import ChartComponent from '../../components/common/ChartComponent';

const CalculateGrades = () => {
  const [myClasses, setMyClasses] = useState([]);
  const [academicSessions, setAcademicSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [calculationResult, setCalculationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [gradeDistribution, setGradeDistribution] = useState(null);

  const calculationSchema = Yup.object().shape({
    academic_session_id: Yup.number().required('Academic session is required'),
    term_id: Yup.number().required('Term is required'),
    class_id: Yup.number().required('Class is required'),
    include_pending: Yup.boolean().default(true),
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedSession) {
      fetchTerms(selectedSession);
    }
  }, [selectedSession]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [classesRes, sessionsRes] = await Promise.all([
        formMasterAPI.getMyClasses(),
        formMasterAPI.getAcademicSessions(),
        // commonAPI.getAcademicSessions(),
      ]);

      setMyClasses(classesRes.data);
      setAcademicSessions(sessionsRes.data);

      // Set defaults
      if (classesRes.data.length > 0) {
        setSelectedClass(classesRes.data[0].class_id);
      }
      if (sessionsRes.data.length > 0) {
        setSelectedSession(sessionsRes.data[0].id);
      }

    } catch (error) {
      toast.error('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTerms = async (sessionId) => {
    try {
      const response = await formMasterAPI.getTerms(sessionId);
      setTerms(response.data);
      if (response.data.length > 0) {
        setSelectedTerm(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load terms');
    }
  };


const calculateGrades = async (values) => {
  try {
    setLoading(true);
    const response = await formMasterAPI.calculateGrades(values);
    setCalculationResult(response.data);
    
    // Generate grade distribution based on actual data
    if (response.data.student_scores) {
      generateGradeDistribution(response.data.student_scores);
    }
    
    toast.success('Grades calculated successfully!');
    setShowPreviewModal(true);
  } catch (error) {
    toast.error(error.response?.data?.detail || 'Failed to calculate grades');
  } finally {
    setLoading(false);
  }
};

// Update generateGradeDistribution to use real data
const generateGradeDistribution = (studentScores) => {
  if (!studentScores || studentScores.length === 0) {
    // Mock data if no real data
    const distribution = {
      'A+': 0,
      'A': 0,
      'B': 0,
      'C': 0,
      'D': 0,
      'F': 0,
    };
    
    setGradeDistribution({
      labels: Object.keys(distribution),
      datasets: [{
        data: Object.values(distribution),
        backgroundColor: [
          '#10b981', '#34d399', '#60a5fa', '#fbbf24', '#f97316', '#ef4444',
        ],
      }]
    });
    return;
  }
  
  // Calculate actual grade distribution
  const distribution = {
    'A+': 0,
    'A': 0,
    'B': 0,
    'C': 0,
    'D': 0,
    'F': 0,
  };
  
  studentScores.forEach(student => {
    const grade = calculateGrade(student.average);
    distribution[grade] = (distribution[grade] || 0) + 1;
  });
  
  setGradeDistribution({
    labels: Object.keys(distribution),
    datasets: [{
      data: Object.values(distribution),
      backgroundColor: [
        '#10b981', '#34d399', '#60a5fa', '#fbbf24', '#f97316', '#ef4444',
      ],
    }]
  });
};

  const getCurrentClass = () => {
    return myClasses.find(cls => cls.class_id == selectedClass);
  };

  const getGradeColor = (grade) => {
    const gradeColors = {
      'A+': 'bg-green-100 text-green-800',
      'A': 'bg-green-100 text-green-800',
      'B': 'bg-blue-100 text-blue-800',
      'C': 'bg-yellow-100 text-yellow-800',
      'D': 'bg-orange-100 text-orange-800',
      'F': 'bg-red-100 text-red-800',
    };
    return gradeColors[grade] || 'bg-gray-100 text-gray-800';
  };

  const calculateGrade = (score) => {
    if (score >= 90) return 'A+';
    if (score >= 80) return 'A';
    if (score >= 70) return 'B';
    if (score >= 60) return 'C';
    if (score >= 50) return 'D';
    return 'F';
  };

  const handleFinalizeGrades = async () => {
    try {
      // API call to finalize grades
      toast.success('Grades finalized successfully!');
      setShowPreviewModal(false);
    } catch (error) {
      toast.error('Failed to finalize grades');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Calculate Student Grades</h1>
          <p className="text-gray-600">Compute final grades and positions for your class</p>
        </div>
      </div>

      {/* Calculation Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Grade Calculation Parameters</h3>
        
        <Formik
          initialValues={{
            academic_session_id: selectedSession,
            term_id: selectedTerm,
            class_id: selectedClass,
            include_pending: true,
          }}
          validationSchema={calculationSchema}
          onSubmit={calculateGrades}
          enableReinitialize
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Class *
                  </label>
                  <Field as="select" name="class_id" className="input-field">
                    <option value="">Choose a class</option>
                    {myClasses.map((cls) => (
                      <option key={cls.class_id} value={cls.class_id}>
                        {cls.class_name} ({cls.student_count} students)
                      </option>
                    ))}
                  </Field>
                  {errors.class_id && touched.class_id && (
                    <div className="text-red-500 text-sm mt-1">{errors.class_id}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Academic Session *
                  </label>
                  <Field as="select" name="academic_session_id" className="input-field">
                    <option value="">Select Session</option>
                    {academicSessions.map((session) => (
                      <option key={session.id} value={session.id}>
                        {session.academic_session}
                      </option>
                    ))}
                  </Field>
                  {errors.academic_session_id && touched.academic_session_id && (
                    <div className="text-red-500 text-sm mt-1">{errors.academic_session_id}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Term *
                  </label>
                  <Field as="select" name="term_id" className="input-field">
                    <option value="">Select Term</option>
                    {terms.map((term) => (
                      <option key={term.id} value={term.id}>
                        {term.term}
                      </option>
                    ))}
                  </Field>
                  {errors.term_id && touched.term_id && (
                    <div className="text-red-500 text-sm mt-1">{errors.term_id}</div>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <Field
                  type="checkbox"
                  name="include_pending"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="include_pending" className="ml-2 text-sm text-gray-700">
                  Include students with pending assessment scores
                </label>
              </div>

              <div className="pt-4 border-t">
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting || loading}
                    className="btn-primary px-6 py-2"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Calculating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-calculator mr-2"></i>
                        Calculate Grades
                      </>
                    )}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>

        {selectedClass && selectedSession && selectedTerm && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center">
              <i className="fas fa-info-circle text-blue-500 mr-2"></i>
              <div>
                <p className="text-blue-700">
                  Ready to calculate grades for <strong>{getCurrentClass()?.class_name}</strong>
                  <span> for <strong>{selectedSession} - {selectedTerm}</strong></span>
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  This will compute final scores, averages, and class positions for all students.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Calculation Preview Modal */}
    {/* Calculation Preview Modal */}
<Modal
  isOpen={showPreviewModal}
  onClose={() => setShowPreviewModal(false)}
  title="Grade Calculation Preview"
  size="xl"
>
  {calculationResult && (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center">
          <div>
            <h4 className="font-medium text-gray-900">Calculation Complete!</h4>
            <p className="text-sm text-gray-600">
              {calculationResult.total_students} students processed successfully
            </p>
            {calculationResult.statistics?.total_students && (
              <p className="text-xs text-gray-500 mt-1">
                {calculationResult.statistics.total_students} students included in statistics
              </p>
            )}
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-green-600">
              Top Student: {calculationResult.statistics?.top_student || calculationResult.top_student}
            </div>
            <div className="text-sm text-gray-600">
              Highest Average Score: {calculationResult.statistics?.highest_score?.score || 'N/A'}%
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">
            {calculationResult.statistics?.highest_score?.score || '0.0'}
          </div>
          <div className="text-sm text-gray-500">Highest Score</div>
          {calculationResult.statistics?.highest_score?.student_name && (
            <div className="text-xs text-gray-400 mt-1 truncate" title={calculationResult.statistics.highest_score.student_name}>
              {calculationResult.statistics.highest_score.student_name}
            </div>
          )}
          {calculationResult.statistics?.highest_score?.student_reg && (
            <div className="text-xs text-gray-400 truncate">
              {calculationResult.statistics.highest_score.student_reg}
            </div>
          )}
        </div>
        
        <div className="border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">
            {calculationResult.statistics?.class_average || calculationResult.class_average || '0.0'}
          </div>
          <div className="text-sm text-gray-500">Class Average</div>
          <div className="text-xs text-gray-400 mt-1">
            {calculationResult.statistics?.total_students || calculationResult.total_students || 0} students
          </div>
        </div>
        
        <div className="border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-yellow-600">
            {calculationResult.statistics?.lowest_score?.score || '0.0'}
          </div>
          <div className="text-sm text-gray-500">Lowest Average Score</div>
          {calculationResult.statistics?.lowest_score?.student_name && (
            <div className="text-xs text-gray-400 mt-1 truncate" title={calculationResult.statistics.lowest_score.student_name}>
              {calculationResult.statistics.lowest_score.student_name}
            </div>
          )}
          {calculationResult.statistics?.lowest_score?.student_reg && (
            <div className="text-xs text-gray-400 truncate">
              {calculationResult.statistics.lowest_score.student_reg}
            </div>
          )}
        </div>
        
        <div className="border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">
            {calculationResult.statistics?.standard_deviation || '0.0'}
          </div>
          <div className="text-sm text-gray-500">Standard Deviation</div>
          <div className="text-xs text-gray-400 mt-1">
            Score spread indicator
          </div>
        </div>
      </div>

      {/* Student Scores Table */}
      {calculationResult.student_scores && calculationResult.student_scores.length > 0 && (
        <div className="mt-6">
          <h4 className="font-medium text-gray-800 mb-3">Student Scores Breakdown</h4>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Score
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Average
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subjects
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject Breakdown
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {calculationResult.student_scores
                  .sort((a, b) => b.total_score - a.total_score)
                  .map((student, index) => {
                    // Calculate grade
                    const grade = calculateGrade(student.average);
                    // Count pending subjects
                    const pendingSubjects = student.subject_scores ? 
                      Object.values(student.subject_scores).filter(subj => subj.status === 'pending').length : 0;
                    const completedSubjects = student.subject_count - pendingSubjects;
                    
                    return (
                      <React.Fragment key={student.student_reg}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              index === 0 ? 'bg-yellow-100 text-yellow-800' :
                              index === 1 ? 'bg-gray-100 text-gray-800' :
                              index === 2 ? 'bg-orange-100 text-orange-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {index + 1}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-medium text-gray-900">{student.full_name}</div>
                              <div className="text-sm text-gray-500">{student.student_reg}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-bold text-gray-900">{student.total_score.toFixed(1)}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center">
                              <span className={`font-bold ${
                                student.average >= 70 ? 'text-green-600' :
                                student.average >= 50 ? 'text-blue-600' :
                                'text-red-600'
                              }`}>
                                {student.average.toFixed(1)}%
                              </span>
                              <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getGradeColor(grade)}`}>
                                {grade}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm">
                              <div className="text-gray-900">{completedSubjects} completed</div>
                              {pendingSubjects > 0 && (
                                <div className="text-yellow-600">{pendingSubjects} pending</div>
                              )}
                              <div className="text-gray-500 text-xs">Total: {student.subject_count}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => {
                                // Toggle subject details view
                                const detailsRow = document.getElementById(`details-${student.student_reg}`);
                                if (detailsRow) {
                                  detailsRow.classList.toggle('hidden');
                                }
                              }}
                              className="text-primary-600 hover:text-primary-900 text-sm"
                            >
                              <i className="fas fa-eye mr-1"></i>
                              View Details
                            </button>
                          </td>
                        </tr>
                        
                        {/* Subject Details Row */}
                        <tr id={`details-${student.student_reg}`} className="hidden bg-gray-50">
                          <td colSpan="6" className="px-4 py-4">
                            <div className="text-sm font-medium text-gray-700 mb-2">
                              Subject Scores for {student.full_name}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {student.subject_scores && Object.entries(student.subject_scores).map(([subjectId, subject]) => (
                                <div 
                                  key={subjectId} 
                                  className={`border rounded p-3 ${
                                    subject.status === 'pending' ? 'bg-yellow-50 border-yellow-200' : 'bg-white'
                                  }`}
                                >
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <div className="font-medium text-gray-900">
                                        {subject.subject_name}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        Code: {subject.subject_code}
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className={`text-lg font-bold ${
                                        subject.status === 'pending' ? 'text-yellow-600' :
                                        subject.score >= 50 ? 'text-green-600' : 'text-red-600'
                                      }`}>
                                        {subject.score.toFixed(1)}
                                      </div>
                                      {subject.status === 'pending' && (
                                        <div className="text-xs text-yellow-600 mt-1">
                                          <i className="fas fa-clock mr-1"></i>
                                          Pending
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Summary */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="flex justify-between text-sm">
                                <div className="text-gray-600">
                                  <span className="font-medium">Subjects Completed:</span> {completedSubjects}
                                </div>
                                <div className="text-yellow-600">
                                  <span className="font-medium">Pending Assessment:</span> {pendingSubjects}
                                </div>
                                <div className="text-gray-800">
                                  <span className="font-medium">Total Subjects:</span> {student.subject_count}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>
          
          {/* Summary of Pending Students */}
          {calculationResult.student_scores.some(s => 
            s.subject_scores && Object.values(s.subject_scores).some(subj => subj.status === 'pending')
          ) && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <i className="fas fa-exclamation-triangle text-yellow-500 mt-0.5 mr-3"></i>
                <div>
                  <h4 className="font-medium text-yellow-800 mb-1">Pending Assessments Detected</h4>
                  <p className="text-sm text-yellow-700">
                    Some students have subjects with pending assessment scores. These were included with a score of 0.
                    Consider finalizing these assessments before publishing final grades.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={() => setShowPreviewModal(false)}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Close Preview
        </button>
        <button
          type="button"
          onClick={() => {
            // Print or export functionality
            window.print();
          }}
          className="px-4 py-2 border border-blue-300 rounded-lg text-blue-700 hover:bg-blue-50"
        >
          <i className="fas fa-print mr-2"></i>
          Print Preview
        </button>
        <button
          type="button"
          onClick={handleFinalizeGrades}
          className="btn-primary px-4 py-2"
        >
          <i className="fas fa-check mr-2"></i>
          Finalize Grades
        </button>
      </div>
    </div>
  )}
</Modal>
      {/* Information Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">How Grade Calculation Works</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-700 mb-2">Calculation Process</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <i className="fas fa-check-circle text-green-500 mt-1 mr-2"></i>
                <span>Retrieves all assessment scores for each student</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-check-circle text-green-500 mt-1 mr-2"></i>
                <span>Applies field weights to calculate total scores</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-check-circle text-green-500 mt-1 mr-2"></i>
                <span>Computes average scores per student</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-check-circle text-green-500 mt-1 mr-2"></i>
                <span>Ranks students based on total scores</span>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-700 mb-2">Important Notes</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <i className="fas fa-exclamation-triangle text-yellow-500 mt-1 mr-2"></i>
                <span>Students with no assessment scores will be excluded</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-exclamation-triangle text-yellow-500 mt-1 mr-2"></i>
                <span>Calculation may take several minutes for large classes</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-exclamation-triangle text-yellow-500 mt-1 mr-2"></i>
                <span>Review the preview before finalizing grades</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-exclamation-triangle text-yellow-500 mt-1 mr-2"></i>
                <span>Finalized grades cannot be easily modified</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Previous Calculations */}
      {/* <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Recent Grade Calculations</h3>
          <button className="text-sm text-primary-600 hover:text-primary-700">
            View all
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Class & Session
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Students
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Avg Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[
                { date: '2023-12-10', class: '10A', session: '2023-2024 Term 2', students: 24, avg: 78.5, status: 'Finalized' },
                { date: '2023-09-15', class: '10B', session: '2023-2024 Term 1', students: 22, avg: 75.2, status: 'Finalized' },
                { date: '2023-06-20', class: '10A', session: '2022-2023 Term 3', students: 24, avg: 80.1, status: 'Finalized' },
                { date: '2023-03-12', class: '10B', session: '2022-2023 Term 2', students: 22, avg: 76.8, status: 'Finalized' },
              ].map((calc, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {calc.date}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{calc.class}</div>
                    <div className="text-sm text-gray-500">{calc.session}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {calc.students} students
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {calc.avg}
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      {calc.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div> */}


    </div>
  );
};

export default CalculateGrades;